// AppSidebar.jsx
import React, { useContext, useState, useEffect, useRef } from 'react';

import {
  Box,
  Drawer,
  IconButton,
  InputBase,
  Typography,
  Avatar,
  Divider,
  Badge,
  Tooltip,
  Button
} from '@mui/material';
import {
  Search as SearchIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Phone
} from '@mui/icons-material';
import { UserContext } from 'src/Contexts/UserContext';
import { AppSidebarNav } from './AppSidebarNav';
import navigation from '../_nav';
import sidebarTheme from 'src/sidebarTheme';
import { isBackGroundWhite } from 'src/sidebarTheme';
import useBreakpoints from 'src/utils/Breakpoints';

const SearchNavGroup = ({ handleSearch = () => { console.log("search") }, backGroundWhite }) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const theme = sidebarTheme;

  function colorGiver(argFocused, argbackGroundWhite) {
    if (argFocused && !argbackGroundWhite) {
      return {
        backgroundColor: theme.colors.text.low,
        borderColor: theme.colors.text.muted,
        searchIconColor: theme.colors.text.secondary,
      }
    }
    if (argFocused && argbackGroundWhite) {
      return {
        backgroundColor: theme.colors.text.muted,
        borderColor: theme.colors.text.muted,
        searchIconColor: theme.colors.primary.high,
      }
    }
    else if (!argFocused && !argbackGroundWhite) {
      return {
        backgroundColor: theme.colors.text.faint,
        borderColor: theme.colors.text.low,
        searchIconColor: theme.colors.text.muted
      }
    }
    else if (!argFocused && argbackGroundWhite) {
      return {
        backgroundColor: theme.colors.text.low,
        borderColor: theme.colors.text.muted,
        searchIconColor: theme.colors.text.muted
      }
    }
    else {
      return {
        backgroundColor: 'rgba(255, 255 ,255, 0.06)',
        borderColor: 'rgba(255, 255 ,255, 0.2)',
        searchIconColor: "rgba(255, 255, 255, 0.9)",
      }
    }
  }
  const colorObj = colorGiver(isFocused, backGroundWhite)

  return (
    <Box sx={{
      padding: '2px',
      mx: 1.5,
      backgroundColor: colorObj.backgroundColor,
      borderRadius: '16px',
      border: `1px solid ${colorObj.borderColor}`,
      transition: theme.transitions.default,
      boxShadow: 'none',
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        borderRadius: '12px',
        padding: '8px 12px',
      }}>
        <SearchIcon sx={{
          color: colorObj.searchIconColor,
          fontSize: 18,
          transition: 'color 0.3s ease'
        }} />
        <InputBase
          placeholder="Search..."
          inputRef={inputRef}
          onChange={handleSearch}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          sx={{
            ml: 1.5,
            flex: 1,
            fontSize: '14px',
            color: colorObj.searchIconColor,
            '& .MuiInputBase-input': {
              padding: '4px 0',
              transition: theme.transitions.default,
              '&::placeholder': {
                color: colorObj.searchIconColor,
                opacity: 1,
                transition: 'color 0.3s ease'
              }
            }
          }}
        />
      </Box>
    </Box>
  );
};

const AppSidebar = ({ sideBarFlag, setSideBarFlag, expandedMenu, setExpandedMenu, handleFooterFormOpen = () => { console.log("this function opens footer form"); } }) => {
  const { contextUserData } = useContext(UserContext);
  const [newNavigation, setNewNavigation] = useState([]);
  const [searchNavigation, setSearchNavigation] = useState([]);
  const [activeMenuIndex, setActiveMenuIndex] = useState(expandedMenu);
  const [shouldResetMenus, setShouldResetMenus] = useState(false);
  const theme = sidebarTheme;
  const { isLg } = useBreakpoints();

  const isTemporary = !isLg;
  const isExpanded = sideBarFlag || isTemporary;
  const isCollapsed = !sideBarFlag && !isTemporary;

  useEffect(() => {
    if (activeMenuIndex !== expandedMenu) {
      setExpandedMenu(activeMenuIndex);
    }
  }, [activeMenuIndex, expandedMenu, setExpandedMenu]);

  useEffect(() => {
    setActiveMenuIndex(expandedMenu);
  }, [expandedMenu]);

  useEffect(() => {
    if (contextUserData) {
      try {
        const { normalSideBarArray, searchFeatSideBarArray } = navigation(contextUserData);
        if (normalSideBarArray?.length > 0) {

          setNewNavigation(normalSideBarArray);
          setSearchNavigation(searchFeatSideBarArray);
        }
      } catch (e) {
        console.error('Error loading navigation:', e);
      }
    }
  }, [contextUserData]);

  useEffect(() => {
    if (isCollapsed) {
      setShouldResetMenus(true);
    }
  }, [isCollapsed]);

  function handleSearch(e) {
    const searchText = e.target.value;
    if (searchText === "") {
      setNewNavigation(navigation(contextUserData)?.normalSideBarArray || []);
    }
    if (searchText !== "" && searchNavigation?.length > 0) {
      const newFilteredArray = searchNavigation.filter((item) =>
        item.name.toLowerCase().includes(searchText.toLowerCase())
      );
      setNewNavigation(newFilteredArray);
    }
  }

  const handleMenuItemClick = (index) => {
    setActiveMenuIndex(index);
    if (!sideBarFlag) {
      setSideBarFlag(true);
      return
    }

    if (isTemporary) {
      setSideBarFlag(false);

    }
  };

  const handlePhoneIconClick = () => {
    setSideBarFlag(true);
  };

  return (
    <Drawer
      variant={isTemporary ? 'temporary' : 'permanent'}
      anchor={isTemporary ? 'top' : 'left'}
      open={isTemporary ? sideBarFlag : true}
      onClose={() => setSideBarFlag(false)}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: isLg ? (sideBarFlag ? 270 : 76) : 0,
        height: isTemporary ? '100vh' : '100%',
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: isLg ? (sideBarFlag ? 270 : 76) : '100vw',
          height: isTemporary ? '100vh' : '100%',
          boxSizing: 'border-box',
          backgroundColor: isCollapsed ? theme.colors.background.collapsed : theme.colors.background.expanded,
          color: theme.colors.text.primary,
          transition: 'width 0.2s cubic-bezier(0.4, 0, 0.2, 1), height 0s',
          borderRight: isLg ? '1px solid rgba(0, 0, 0, 0.15)' : 'none',
          borderBottom: isTemporary ? '1px solid rgba(0, 0, 0, 0.15)' : 'none',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
      }}>
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(25, 118, 210, 0.02)',
          zIndex: 0,
        }} />

        <Box sx={{
          py: 2,
          px: 1,
          mt: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: "center",
          gap: 0.1,
          position: 'relative',
          zIndex: 1,
        }}>
          <IconButton
            onClick={() => setSideBarFlag(!sideBarFlag)}
            sx={{
              color: theme.colors.text.primary,
              backgroundColor: theme.colors.background.hover,
              borderRadius: '50%',
              padding: '8px',
              transition: theme.transitions.default,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: isBackGroundWhite ? theme.colors.text.primary : theme.colors.text.primary,
                color: theme.colors.primary.text,
                transform: 'scale(1.05)'
              },
              '& .MuiSvgIcon-root': {
                fontSize: '1.2rem',
                transition: 'transform 0.3s ease',
                transform: sideBarFlag ? 'rotate(0deg)' : 'rotate(180deg)'
              }
            }}
          >
            {isExpanded ? <ChevronLeftIcon fontSize='30px' /> : <MenuIcon fontSize='30px' />}
          </IconButton>

          {isExpanded && (
            <Box sx={{
              flex: 1,
              opacity: isExpanded ? 1 : 0,
              transform: isExpanded ? 'translateX(0)' : 'translateX(-20px)',
              transition: theme.transitions.default,
              position: 'relative',
              zIndex: 1,
            }}>
              <SearchNavGroup handleSearch={handleSearch} backGroundWhite={isBackGroundWhite} />
            </Box>
          )}
        </Box>

        <Divider sx={{
          my: sideBarFlag ? 1 : 0.5,
          borderColor: theme.colors.divider,
          mx: 1.5,
          opacity: 0.8,
          transition: 'opacity 0.3s ease',
          position: 'relative',
          zIndex: 1,
        }} />

        <Box
          sx={{
            flexGrow: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            pl: sideBarFlag ? 0 : 0,
            pr: sideBarFlag ? 2 : 0,
            py: 1,
            transition: theme.transitions.default,
            position: 'relative',
            zIndex: 1,
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: theme.colors.scrollbar.thumb,
              borderRadius: '4px',
            },
            '&:hover::-webkit-scrollbar-thumb': { background: theme.colors.scrollbar.thumbHover },
          }}
        >
          <AppSidebarNav
            items={newNavigation}
            collapsed={isCollapsed}
            onMenuItemClick={handleMenuItemClick}
            activeMenuIndex={activeMenuIndex}
            setActiveMenuIndex={setActiveMenuIndex}
            shouldResetMenus={shouldResetMenus}
            onMenusReset={() => setShouldResetMenus(false)}
          />
        </Box>

        <Box
          sx={{
            p: 1,
            borderTop: `1px solid ${theme.colors.divider}`,
            pt: '10px',
            mx: 1.5,
            position: 'relative',
            zIndex: 1,
            backgroundColor: "inherit",
            transition: theme.transitions.default,
            display: 'flex',
            flexDirection: 'column',
            alignItems: isExpanded ? 'start' : 'center',
            justifyContent: 'start',
          }}
        >
          {isExpanded ? (
            <>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                ml: 0,
                gap: '8px',
                mb: 0.9
              }}>
                <Box sx={{
                  color: isBackGroundWhite ? theme.colors.primary.main : theme.colors.text.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: theme.colors.background.hover,
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  flexShrink: 0,
                  transition: theme.transitions.default,
                  '&:hover': {
                    backgroundColor: theme.colors.background.navGroupHover,
                    color: isBackGroundWhite ? theme.colors.background.main : theme.colors.primary.text,
                    transform: 'scale(1.05)'
                  }
                }}>
                  <Phone sx={{ fontSize: '16px' }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', minWidth: 0 }}>
                  <Typography variant="body2" sx={{
                    fontSize: '12px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                    color: theme.colors.text.secondary,
                    marginRight: '4px'
                  }}>
                    For Queries Call
                  </Typography>
                  <Typography variant="body1" sx={{
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: theme.transitions.default,
                    color: theme.colors.text.primary,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    '&:hover': { textDecoration: 'underline' }
                  }}>
                    +91 9714105866
                  </Typography>
                </Box>
              </Box>
              <Button
                onClick={handleFooterFormOpen}
                sx={{
                  width: '100%',
                  fontSize: '14px',
                  fontWeight: "500",
                  textTransform: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  backgroundColor: theme.colors.background.hover,
                  color: theme.colors.text.primary,
                  border: 'none',
                  cursor: 'pointer',
                  transition: theme.transitions.default,
                  '&:hover': {
                    backgroundColor: isBackGroundWhite ? theme.colors.primary.main : theme.colors.background.navGroupHover,
                    color: isBackGroundWhite ? theme.colors.primary.text : theme.colors.primary.text,
                    boxShadow: `0px 2px 8px ${theme.colors.primary.hover}`,
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                Register Grievance
              </Button>
            </>
          ) : (
            <Tooltip title="For Queries: +91 9714105866" placement="right">
              <IconButton
                onClick={handlePhoneIconClick}
                sx={{
                  color: isBackGroundWhite ? theme.colors.primary.main : theme.colors.text.primary,
                  backgroundColor: theme.colors.background.hover,
                  borderRadius: '50%',
                  width: '38px',
                  height: '38px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: theme.transitions.default,
                  '&:hover': {
                    backgroundColor: theme.colors.background.navGroupHover,
                    color: isBackGroundWhite ? theme.colors.background.main : theme.colors.primary.text,
                    transform: 'scale(1.05)'
                  }
                }}
              >
                <Phone sx={{ fontSize: '20px' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default AppSidebar;